import React, { useState } from 'react';
import { menuService } from '../../services/dataService';
import { supabase } from '../../services/supabase';
import { getProxiedImageUrl } from '../../utils/imageUtils';

const MenuManagement = ({ restaurant, menu, onDataChange }) => {
  // Modal states
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showEditItem, setShowEditItem] = useState(false);
  const [showEditCategory, setShowEditCategory] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);

  // Form states
  const [categoryForm, setCategoryForm] = useState({ name: '' });
  const [itemForm, setItemForm] = useState({
  name: '',
  description: '',
  price: '',
  currency: restaurant?.default_currency || '₹',
  image_file: null,
  image_url: '',
  category_id: '',
  is_available: true,
  food_type: 'veg'  // ADD THIS LINE
});
  const [itemErrors, setItemErrors] = useState({});
  const [uploading, setUploading] = useState(false);

  // Helper function to delete old image from Supabase Storage
  const deleteImageFromSupabase = async (imageUrl) => {
    try {
      if (!imageUrl || !imageUrl.includes('supabase')) {
        console.log('No valid Supabase image URL to delete:', imageUrl);
        return;
      }

      const urlParts = imageUrl.split('/');
      const bucketIndex = urlParts.findIndex(part => part === 'restaurant-images');

      if (bucketIndex === -1) {
        console.error('Invalid image URL format - bucket not found:', imageUrl);
        return;
      }

      const filePath = urlParts.slice(bucketIndex + 1).join('/');
      console.log('Attempting to delete file path:', filePath);

      const { error } = await supabase.storage
        .from('restaurant-images')
        .remove([filePath]);

      if (error) {
        console.error('Supabase storage deletion error:', error);
        throw error;
      }

      console.log('Image deleted successfully from storage:', filePath);
    } catch (error) {
      console.error('Error in deleteImageFromSupabase:', error);
      throw error;
    }
  };

  // Image upload to Supabase Storage
  const uploadImageToSupabase = async (file) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `menu-images/${fileName}`;

      const { data, error } = await supabase.storage
        .from('restaurant-images')
        .upload(filePath, file);

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('restaurant-images')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setItemErrors({ ...itemErrors, image: 'Please select a valid image file' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setItemErrors({ ...itemErrors, image: 'Image size must be less than 5MB' });
      return;
    }

    setItemForm({ ...itemForm, image_file: file });
    setItemErrors({ ...itemErrors, image: '' });
  };

  const validateItemForm = () => {
    const errors = {};
    if (!itemForm.name.trim()) errors.name = 'Name is required';
    if (!itemForm.price || parseFloat(itemForm.price) <= 0) errors.price = 'Valid price is required';
    if (!itemForm.category_id) errors.category_id = 'Category is required';
    return errors;
  };

  // Category Management
  const handleAddCategory = async () => {
    if (!categoryForm.name.trim()) {
      alert('Category name is required');
      return;
    }

    try {
      const { error } = await menuService.createCategory({
        restaurant_id: restaurant.id,
        name: categoryForm.name.trim(),
        display_order: menu.length + 1
      });

      if (error) throw error;

      setCategoryForm({ name: '' });
      setShowAddCategory(false);
      onDataChange();
    } catch (error) {
      console.error('Error adding category:', error);
      alert('Failed to add category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
  if (!window.confirm('Are you sure? This will delete all items in this category.')) {
    return;
  }

  try {
    console.log('About to delete category:', categoryId);

    const category = menu.find(cat => cat.id === categoryId);
    if (category && category.menu_items) {
      for (const item of category.menu_items) {
        if (item.image_url) {
          await deleteImageFromSupabase(item.image_url);
        }
      }
    }

    const { data, error } = await menuService.deleteCategory(categoryId);
    console.log('Delete result:', { data, error });

    if (error) {
      console.error('Delete error:', error);
      throw error;
    }

    console.log('Category deleted, reloading data...');
    await onDataChange();

  } catch (error) {
    console.error('Error deleting category:', error);
    alert('Failed to delete category: ' + error.message);
  }
};
  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({ name: category.name });
    setShowEditCategory(true);
  };

  const handleUpdateCategory = async () => {
    if (!categoryForm.name.trim()) {
      alert('Category name is required');
      return;
    }

    try {
      const { error } = await menuService.updateCategory(editingCategory.id, {
        name: categoryForm.name.trim()
      });

      if (error) throw error;

      setCategoryForm({ name: '' });
      setEditingCategory(null);
      setShowEditCategory(false);
      onDataChange();
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category');
    }
  };
  // Item Management
  const handleAddItem = async () => {
    const errors = validateItemForm();
    if (Object.keys(errors).length > 0) {
      setItemErrors(errors);
      return;
    }

    setUploading(true);
    try {
      let imageUrl = itemForm.image_url;

      if (itemForm.image_file) {
        imageUrl = await uploadImageToSupabase(itemForm.image_file);
      }

      const { error } = await menuService.createMenuItem({
        restaurant_id: restaurant.id,
        category_id: itemForm.category_id,
        name: itemForm.name.trim(),
        description: itemForm.description.trim(),
        price: parseFloat(itemForm.price),
        currency: restaurant?.default_currency || '₹',
        image_url: imageUrl,
        is_available: itemForm.is_available,
        food_type: itemForm.food_type
      });

      if (error) throw error;

      setItemForm({
        name: '',
        description: '',
        price: '',
        currency: '$',
        image_file: null,
        image_url: '',
        category_id: '',
        is_available: true,
        food_type: 'veg'
      });
      setItemErrors({});
      setShowAddItem(false);
      onDataChange();
    } catch (error) {
      console.error('Error adding item:', error);
      alert('Failed to add item. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleEditItem = (item) => {
  setEditingItem(item);
  setItemForm({
    name: item.name,
    description: item.description || '',
    price: item.price.toString(),
    currency: item.currency || '$',
    image_file: null,
    image_url: item.image_url || '',
    category_id: item.category_id,
    is_available: item.is_available,
    food_type: item.food_type || 'veg'  // ADD THIS LINE
  });
  setItemErrors({});
  setShowEditItem(true);
};

  const handleUpdateItem = async () => {
    const errors = validateItemForm();
    if (Object.keys(errors).length > 0) {
      setItemErrors(errors);
      return;
    }

    setUploading(true);
    try {
      let imageUrl = itemForm.image_url;

      if (itemForm.image_file) {
        // First upload the new image
        const newImageUrl = await uploadImageToSupabase(itemForm.image_file);
         // Only delete the old image after successful upload
        if (editingItem.image_url && editingItem.image_url !== newImageUrl) {
          try {
            await deleteImageFromSupabase(editingItem.image_url);
            console.log('Old image deleted successfully');
          } catch (deleteError) {
            console.error('Failed to delete old image:', deleteError);
            // Don't fail the update if old image deletion fails
            }
  }

  imageUrl = newImageUrl;
}
      const { error } = await menuService.updateMenuItem(editingItem.id, {
        name: itemForm.name.trim(),
        description: itemForm.description.trim(),
        price: parseFloat(itemForm.price),
        currency: restaurant?.default_currency || '₹',
        currency: itemForm.currency,
        image_url: imageUrl,
        is_available: itemForm.is_available,
        food_type: itemForm.food_type
      });

      if (error) throw error;

      setEditingItem(null);
      setItemErrors({});
      setShowEditItem(false);
      onDataChange();
    } catch (error) {
      console.error('Error updating item:', error);
      alert('Failed to update item. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteItem = async (itemId, itemName) => {
  if (!window.confirm(`Are you sure you want to delete "${itemName}"? It will be hidden from the menu but preserved in order history.`)) return;

  try {
    console.log('Attempting to soft delete item:', itemId, itemName);

    // Soft delete: mark as deleted instead of removing from database
    const { error } = await menuService.updateMenuItem(itemId, {
      deleted: true,
      is_available: false
    });

    if (error) {
      console.error('Database deletion error:', error);
      alert(`Failed to delete item: ${error.message}`);
      return;
    }

    await onDataChange();
    alert(`"${itemName}" has been deleted successfully. Order history is preserved.`);

  } catch (error) {
    console.error('Error deleting item:', error);
    alert(`Failed to delete "${itemName}": ${error.message}`);
  }
};

  return (
    <>
          {/* Menu Management */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: '30px',
            boxShadow: '0 15px 40px rgba(0,0,0,0.1)',
            border: '2px solid rgba(255, 68, 68, 0.1)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '30px',
              paddingBottom: '20px',
              borderBottom: '2px solid #f8f9fa'
            }}>
              <h2 style={{
                margin: '0',
                color: '#333',
                fontSize: '2rem',
                fontWeight: '700'
              }}>
                Menu Management
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  fontWeight: '600'
                }}>
                  {menu.length} Categories
                </div>
                <button
                  onClick={() => setShowAddCategory(true)}
                  style={{
                    background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
                    color: 'white',
                    border: 'none',
                    padding: '15px 25px',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 8px 25px rgba(255, 68, 68, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                  onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                >
                  + Add Category
                </button>
              </div>
            </div>

            <div style={{ maxHeight: '700px', overflowY: 'auto' }}>
              {menu.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 40px',
                  background: '#f8f9fa',
                  borderRadius: '15px',
                  border: '2px dashed #dee2e6'
                }}>
                  <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🍽️</div>
                  <h3 style={{ color: '#666', margin: '0 0 15px 0', fontSize: '1.3rem' }}>
                    No Menu Categories Yet
                  </h3>
                  <p style={{ color: '#888', margin: '0', fontSize: '1rem' }}>
                    Click "Add Category" above to create your first menu category
                  </p>
                </div>
              ) : (
                menu.map(category => (
                  <div key={category.id} style={{
                    border: '2px solid #f1f3f4',
                    borderRadius: '15px',
                    padding: '25px',
                    marginBottom: '25px',
                    backgroundColor: '#fefefe',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.borderColor = '#FF6B00'}
                  onMouseOut={(e) => e.currentTarget.style.borderColor = '#f1f3f4'}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '20px'
                    }}>
                      <div>
                        <h3 style={{
                          margin: '0 0 5px 0',
                          color: '#333',
                          fontSize: '1.5rem',
                          fontWeight: '700'
                        }}>
                          {category.name}
                        </h3>
                        <p style={{
                          margin: '0',
                          color: '#666',
                          fontSize: '0.9rem'
                        }}>
                          {category.menu_items?.length || 0} items in this category
                        </p>
                     </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button
                        onClick={() => handleEditCategory(category)}
                        style={{
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
                      >
                        Edit Category
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(category.id)}
                        style={{
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#c82333'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
                      >
                        Delete Category
                      </button>
                    </div>
                  </div>

                    <div style={{ marginBottom: '20px' }}>
                      {category.menu_items?.map(item => (
                        <div key={item.id} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '15px',
                          backgroundColor: 'white',
                          borderRadius: '12px',
                          marginBottom: '12px',
                          border: '1px solid #e9ecef',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 107, 0, 0.15)'}
                        onMouseOut={(e) => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)'}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            {item.image_url && (
                              <img
                                src={getProxiedImageUrl(item.image_url)}
                                alt={item.name}
                                style={{
                                  width: '60px',
                                  height: '60px',
                                  objectFit: 'cover',
                                  borderRadius: '10px',
                                  border: '2px solid #f1f3f4'
                                }}
                              />
                            )}
                            <div>
                              <div style={{
                                fontWeight: '700',
                                color: '#333',
                                fontSize: '1.1rem',
                                marginBottom: '4px'
                              }}>
                                {item.name}
                              </div>
                              <div style={{
                                fontSize: '1.3rem',
                                fontWeight: '700',
                                color: '#FF6B00',
                                marginBottom: '4px'
                              }}>
                                {item.currency || '$'}{item.price}
                              </div>
                              <div style={{
                                fontSize: '0.8rem',
                                color: item.is_available ? '#28a745' : '#dc3545',
                                fontWeight: '600'
                              }}>
                                {item.is_available ? '● Available' : '● Unavailable'}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => handleEditItem(item)}
                              style={{
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: '600'
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteItem(item.id, item.name)}
                              style={{
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: '600'
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}

                      <button
                        onClick={() => {
                          setItemForm({ ...itemForm, category_id: category.id });
                          setShowAddItem(true);
                        }}
                        style={{
                          width: '100%',
                          padding: '20px',
                          color: '#FF6B00',
                          backgroundColor: 'transparent',
                          border: '3px dashed #FF6B00',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          fontSize: '1.1rem',
                          fontWeight: '700',
                          transition: 'all 0.3s ease',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = '#FFF5F0';
                          e.target.style.borderColor = '#FF4444';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.borderColor = '#FF6B00';
                        }}
                      >
                        + Add Item to {category.name}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

      {/* Add Category Modal */}
{showAddCategory && (
  <div style={{
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '1000'
  }}>
    <div style={{
      background: 'white',
      padding: '30px',
      borderRadius: '15px',
      maxWidth: '400px',
      width: '90%'
    }}>
      <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>Add New Category</h3>
      <input
        type="text"
        placeholder="Category name"
        value={categoryForm.name}
        onChange={(e) => setCategoryForm({ name: e.target.value })}
        style={{
          width: '100%',
          padding: '12px',
          border: '2px solid #e9ecef',
          borderRadius: '8px',
          marginBottom: '20px',
          fontSize: '1rem'
        }}
        autoFocus
      />
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button
          onClick={() => setShowAddCategory(false)}
          style={{
            padding: '10px 20px',
            border: '2px solid #6c757d',
            backgroundColor: 'transparent',
            color: '#6c757d',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleAddCategory}
          style={{
            padding: '10px 20px',
            backgroundColor: '#FF6B00',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          Add Category
        </button>
      </div>
    </div>
  </div>
)}

{/* Add Item Modal */}
{showAddItem && (
  <div style={{
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '1000'
  }}>
    <div style={{
      background: 'white',
      padding: '30px',
      borderRadius: '15px',
      maxWidth: '500px',
      width: '90%'
    }}>
      <h3 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '1.4rem' }}>Add New Item</h3>

      <div style={{ marginBottom: '15px' }}>
        <input
          type="text"
          placeholder="Item name *"
          value={itemForm.name}
          onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
          style={{
            width: '100%',
            padding: '12px',
            border: itemErrors.name ? '2px solid #dc2626' : '2px solid #e9ecef',
            borderRadius: '8px',
            fontSize: '1rem',
            marginBottom: '5px'
          }}
        />
        {itemErrors.name && (
          <div style={{ color: '#dc2626', fontSize: '0.8rem' }}>
            {itemErrors.name}
          </div>
        )}
      </div>

      <div style={{ marginBottom: '15px' }}>
        <textarea
          placeholder="Description"
          value={itemForm.description}
          onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
          rows="3"
          style={{
            width: '100%',
            padding: '12px',
            border: '2px solid #e9ecef',
            borderRadius: '8px',
            fontSize: '1rem',
            resize: 'vertical'
          }}
        />
      </div>


      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
      <div>
        <input
          type="number"
          step="0.01"
          placeholder="Price *"
          value={itemForm.price}
          onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })}
          style={{
            width: '100%',
            padding: '12px',
            border: itemErrors.price ? '2px solid #dc2626' : '2px solid #e9ecef',
            borderRadius: '8px',
            fontSize: '1rem'
          }}
        />
        {itemErrors.price && (
          <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '5px' }}>
            {itemErrors.price}
          </div>
        )}
      </div>


      <div>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
          Currency
        </label>
        <select
          value={restaurant?.default_currency || '₹'}
          disabled
          style={{
            width: '100%',
            padding: '12px',
            border: '2px solid #e9ecef',
            borderRadius: '8px',
            fontSize: '1rem',
            backgroundColor: '#f8f9fa',
            color: '#333',
            fontWeight: '600',
            cursor: 'not-allowed'
          }}
        >
          <option value={restaurant?.default_currency || '₹'}>
            {restaurant?.default_currency || '₹'}
          </option>
        </select>
        <small style={{
          fontSize: '0.85rem',
          color: '#6c757d',
          marginTop: '5px',
          display: 'block'
        }}>
          This is your restaurant's default currency. Change it in Profile Settings.
        </small>
      </div>
    </div>

    {/* ADD THIS NEW SECTION: */}
    <div style={{ marginBottom: '15px' }}>
      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
        Food Type
      </label>
      <select
        value={itemForm.food_type}
        onChange={(e) => setItemForm({ ...itemForm, food_type: e.target.value })}
        style={{
          width: '100%',
          padding: '12px',
          border: '2px solid #e9ecef',
          borderRadius: '8px',
          fontSize: '1rem'
        }}
      >
        <option value="veg">🟢 Vegetarian</option>
        <option value="non_veg">🔴 Non-Vegetarian</option>
      </select>
    </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
          Item Image (optional)
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          style={{
            width: '100%',
            padding: '12px',
            border: '2px solid #e9ecef',
            borderRadius: '8px',
            fontSize: '1rem'
          }}
        />
        {itemErrors.image && (
          <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '5px' }}>
            {itemErrors.image}
          </div>
        )}
        {itemForm.image_file && (
          <div style={{ marginTop: '8px', color: '#28a745', fontSize: '0.9rem' }}>
            Selected: {itemForm.image_file.name}
          </div>
        )}
      </div>

      <div style={{ marginBottom: '25px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={itemForm.is_available}
            onChange={(e) => setItemForm({ ...itemForm, is_available: e.target.checked })}
          />
          <span>Available for ordering</span>
        </label>
      </div>

      <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
        <button
          onClick={() => {
            setShowAddItem(false);
            setItemForm({
              name: '',
              description: '',
              price: '',
              currency: restaurant?.default_currency || '₹',
              image_file: null,
              image_url: '',
              category_id: '',
              is_available: true
            });
            setItemErrors({});
          }}
          style={{
            padding: '12px 24px',
            border: '2px solid #6c757d',
            backgroundColor: 'transparent',
            color: '#6c757d',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleAddItem}
          disabled={uploading}
          style={{
            padding: '12px 24px',
            backgroundColor: uploading ? '#ccc' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: uploading ? 'not-allowed' : 'pointer',
            fontSize: '1rem'
          }}
        >
          {uploading ? 'Adding...' : 'Add Item'}
        </button>
      </div>
    </div>
  </div>
)}

      {/* Edit Item Modal */}
      {showEditItem && editingItem && (
          <div style={{
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: '1000'
          }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '15px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '1.4rem' }}>Edit Item</h3>

            <div style={{ marginBottom: '15px' }}>
              <input
                type="text"
                placeholder="Item name *"
                value={itemForm.name}
                onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: itemErrors.name ? '2px solid #dc2626' : '2px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  marginBottom: '5px'
                }}
              />
              {itemErrors.name && (
                <div style={{ color: '#dc2626', fontSize: '0.8rem' }}>
                  {itemErrors.name}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '15px' }}>
              <textarea
                placeholder="Description"
                value={itemForm.description}
                onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                rows="3"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div>
                <input
                  type="number"
                  step="0.01"
                  placeholder="Price *"
                  value={itemForm.price}
                  onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: itemErrors.price ? '2px solid #dc2626' : '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
                {itemErrors.price && (
                  <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '5px' }}>
                    {itemErrors.price}
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                  Currency
                </label>
                <select
                  value={restaurant?.default_currency || '₹'}
                  disabled
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    backgroundColor: '#f8f9fa',
                    color: '#333',
                    fontWeight: '600',
                    cursor: 'not-allowed'
                  }}
                >
                  <option value={restaurant?.default_currency || '₹'}>
                    {restaurant?.default_currency || '₹'}
                  </option>
                </select>
                <small style={{
                  fontSize: '0.85rem',
                  color: '#6c757d',
                  marginTop: '5px',
                  display: 'block'
                }}>
                  This is your restaurant's default currency. Change it in Profile Settings.
                </small>
              </div>
            </div>

          {/* ADD THIS ENTIRE SECTION AFTER THE PRICE/CURRENCY GRID: */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
              Food Type
            </label>
            <select
              value={itemForm.food_type}
              onChange={(e) => setItemForm({ ...itemForm, food_type: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e9ecef',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
            >
              <option value="veg">🟢 Vegetarian</option>
              <option value="non_veg">🔴 Non-Vegetarian</option>
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
              Item Image (optional)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e9ecef',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
            />
            {itemErrors.image && (
              <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '5px' }}>
                {itemErrors.image}
              </div>
            )}
            {itemForm.image_file && (
              <div style={{ marginTop: '8px', color: '#28a745', fontSize: '0.9rem' }}>
                Selected: {itemForm.image_file.name}
              </div>
            )}
            {itemForm.image_url && !itemForm.image_file && (
              <div style={{ marginTop: '8px', color: '#007bff', fontSize: '0.9rem' }}>
                Current image: {editingItem.name}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={itemForm.is_available}
                onChange={(e) => setItemForm({ ...itemForm, is_available: e.target.checked })}
              />
              <span>Available for ordering</span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                setShowEditItem(false);
                setEditingItem(null);
                setItemForm({
                  name: '',
                  description: '',
                  price: '',
                  currency: '$',
                  image_file: null,
                  image_url: '',
                  category_id: '',
                  is_available: true,
                  food_type: 'veg'
                });
                setItemErrors({});
              }}
              style={{
                padding: '12px 24px',
                border: '2px solid #6c757d',
                backgroundColor: 'transparent',
                color: '#6c757d',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateItem}
              disabled={uploading}
              style={{
                padding: '12px 24px',
                backgroundColor: uploading ? '#ccc' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: uploading ? 'not-allowed' : 'pointer',
                fontSize: '1rem'
              }}
            >
              {uploading ? 'Updating...' : 'Update Item'}
            </button>
          </div>
        </div>
      </div>
    )}

{/* Edit Category Modal */}
{showEditCategory && editingCategory && (
  <div style={{
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '1000'
  }}>
    <div style={{
      background: 'white',
      padding: '30px',
      borderRadius: '15px',
      maxWidth: '400px',
      width: '90%'
    }}>
      <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>Edit Category</h3>
      <input
        type="text"
        placeholder="Category name"
        value={categoryForm.name}
        onChange={(e) => setCategoryForm({ name: e.target.value })}
        style={{
          width: '100%',
          padding: '12px',
          border: '2px solid #e9ecef',
          borderRadius: '8px',
          marginBottom: '20px',
          fontSize: '1rem'
        }}
        autoFocus
      />
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button
          onClick={() => {
            setShowEditCategory(false);
            setEditingCategory(null);
            setCategoryForm({ name: '' });
          }}
          style={{
            padding: '10px 20px',
            border: '2px solid #6c757d',
            backgroundColor: 'transparent',
            color: '#6c757d',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleUpdateCategory}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          Update Category
        </button>
      </div>
    </div>
  </div>
)}
    </>
  );
};

export default MenuManagement;
